import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import './DraggableTileGrid.scss';

// Swiper v6 imports - Grid might not be available in v6
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';

// Components
import TileGridHeader from './components/TileGridHeader';

const DraggableTileGrid = ({
  items = [],
  onItemClick = () => {},
  title = '',
  itemsPerColumn = 3,
  columnWidth = 162,
  renderItem = (item) => null,
  className = '',
  minColumnsInView = 1,
  viewAllText = '',
  onViewAllClick = () => {},
  maxColumns = null,
  initialOffset = 0,
  mobileSlidesPerView = 2.3,
  slidesOffsetAfter = 0,
  enableNavigation = false,
  desktopSlidesPerView = null,
  customSpaceBetween = null,
}) => {
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const navigationPrevRef = React.useRef(null);
  const navigationNextRef = React.useRef(null);

  // Event handlers
  const handleItemClick = (item) => onItemClick(item);

  const handleSwiperSlideChange = (swiper) => {
    setHasStartedScrolling(true);
    setActiveSlideIndex(swiper.activeIndex);
  };

  const handleSwiperInit = (swiper) => {
    setSwiperInstance(swiper);
    setActiveSlideIndex(swiper.activeIndex);
  };

  // Create grid columns manually
  const createGridColumns = () => {
    const columns = [];
    const totalItems = maxColumns ? Math.min(items.length, maxColumns * itemsPerColumn) : items.length;
    
    for (let i = 0; i < totalItems; i += itemsPerColumn) {
      const columnItems = items.slice(i, i + itemsPerColumn);
      columns.push(columnItems);
    }
    
    return columns;
  };

  const gridColumns = createGridColumns();

  return (
    <div className={`draggable-tile-grid ${className}`}>
      <TileGridHeader
        title={title}
        viewAllText={viewAllText}
        onViewAllClick={onViewAllClick}
      />
      <div className="swiper-container">
        <Swiper
          spaceBetween={customSpaceBetween !== null ? 0 : (className.includes('genres-tile') ? (isMobile ? 16 : 24) : 16)}
          slidesPerView={!isMobile && desktopSlidesPerView ? desktopSlidesPerView : 'auto'}
          allowTouchMove={isMobile && activeSlideIndex < gridColumns.length - 1}
          grabCursor={isMobile && activeSlideIndex < gridColumns.length - 1}
          slidesOffsetBefore={initialOffset}
          slidesOffsetAfter={slidesOffsetAfter}
          onSlideChange={handleSwiperSlideChange}
          onInit={(swiper) => {
            handleSwiperInit(swiper);
            // Assign custom navigation elements after swiper initialization
            if (enableNavigation && swiper.params.navigation) {
              swiper.params.navigation.prevEl = navigationPrevRef.current;
              swiper.params.navigation.nextEl = navigationNextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }
          }}
          resistance={true}
          resistanceRatio={0.85}
          className="tile-swiper"
          navigation={enableNavigation ? {
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          } : false}
        >
          {gridColumns.map((column, columnIndex) => {
            const isLast = columnIndex === gridColumns.length - 1;
            const lastMargin = isLast && slidesOffsetAfter ? slidesOffsetAfter : 0;
            return (
            <SwiperSlide
              key={columnIndex}
              className="tile-column"
              style={{ width: `${columnWidth}px`, marginRight: `${lastMargin}px` }}
            >
              {column.map((item) => (
                <div key={item.id} className="tile-item" onClick={() => handleItemClick(item)}>
                  {renderItem(item)}
                </div>
              ))}
            </SwiperSlide>
          )})}
        </Swiper>
        {enableNavigation && (
          <>
            <div ref={navigationNextRef} className="swiper-button-prev"></div>
            <div ref={navigationPrevRef} className="swiper-button-next"></div>
          </>
        )}
      </div>
    </div>
  );
};

export default DraggableTileGrid;
